import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { ArrowLeft, UtensilsCrossed } from 'lucide-react';
import { InvitationScreen } from './InvitationScreen';
import { LoginRequired } from './LoginRequired';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost } from '../utils/apiClient';
import { normalizeRecipeDetail } from '../utils/normalizeRecipeDetail';

const PENDING_INVITE_KEY = 'mp:pendingInvite';
const isObjectId = (value) => typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value);

export function InvitationPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const inviteType = searchParams.get('type') === 'household' ? 'household' : 'recipe';
  const token = searchParams.get('token') || '';
  const weeklyPlanId = searchParams.get('weeklyPlanId') || searchParams.get('weeklyPlanID') || '';
  const recipeId = searchParams.get('recipeId') || '';
  const sharedByFromQuery = searchParams.get('sharedBy') || '';
  const householdNameFromQuery = searchParams.get('householdName') || '';

  const [sharedBy, setSharedBy] = useState(sharedByFromQuery);
  const [householdName, setHouseholdName] = useState(householdNameFromQuery);

  const [recipe, setRecipe] = useState(null);
  const [loadingRecipe, setLoadingRecipe] = useState(inviteType === 'recipe');
  const [showLoginRequired, setShowLoginRequired] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [recipeAccessState, setRecipeAccessState] = useState(inviteType === 'recipe' ? 'checking' : 'idle');
  const resumeAttemptedRef = useRef(false);
  const hasValidWeeklyPlanId = isObjectId(String(weeklyPlanId || ''));
  const hasValidRecipeId = isObjectId(String(recipeId || ''));

  useEffect(() => {
    setSharedBy(sharedByFromQuery);
    setHouseholdName(householdNameFromQuery);
  }, [sharedByFromQuery, householdNameFromQuery]);

  useEffect(() => {
    if (!token) return;
    if (inviteType !== 'household') return;
    if (sharedByFromQuery && householdNameFromQuery) return;

    let cancelled = false;

    (async () => {
      try {
        const invite = await apiGet(`/public/households/invitations/${encodeURIComponent(token)}`);
        if (cancelled) return;
        if (!sharedByFromQuery && invite?.invitedBy) {
          setSharedBy(invite.invitedBy);
        }
        if (!householdNameFromQuery && invite?.householdName) {
          setHouseholdName(invite.householdName);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to load invite details:', err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inviteType, token, sharedByFromQuery, householdNameFromQuery]);

  // Fetch recipe preview for recipe invites
  useEffect(() => {
    if (inviteType !== 'recipe') return;
    if (!user) {
      setRecipe(null);
      setRecipeAccessState('checking');
      setLoadingRecipe(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingRecipe(true);
        setRecipeAccessState('checking');
        let raw = null;
        let previewError = null;

        if (hasValidWeeklyPlanId) {
          try {
            raw = await apiGet(`/weeklyPlans/${encodeURIComponent(String(weeklyPlanId))}`, {
              headers: { 'X-MP-Invite-Flow': 'recipe-share' },
            });
          } catch (err) {
            previewError = err;
          }
        }

        if (!raw && hasValidRecipeId) {
          try {
            raw = await apiGet(`/recipes/${encodeURIComponent(String(recipeId))}`, {
              headers: { 'X-MP-Invite-Flow': 'recipe-share' },
            });
            previewError = null;
          } catch (err) {
            previewError = err;
          }
        }

        if (!raw && !previewError) {
          throw Object.assign(new Error('No valid recipe share identifier found.'), { status: 400 });
        }

        if (!raw && previewError) {
          throw previewError;
        }

        if (!cancelled && raw) {
          setRecipe(normalizeRecipeDetail(raw));
          setRecipeAccessState('member');
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Failed to load invite recipe preview:', err);
          setRecipe(null);
          if (err?.status === 401) {
            setShowLoginRequired(true);
          } else if (err?.status) {
            setRecipeAccessState(token ? 'join-required' : 'invite-required');
          } else {
            setRecipeAccessState('error');
            toast.error('Unable to load recipe preview');
          }
        }
      } finally {
        if (!cancelled) setLoadingRecipe(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hasValidRecipeId, hasValidWeeklyPlanId, inviteType, weeklyPlanId, recipeId, token, user]);

  const persistPendingInvite = () => {
    const payload = {
      inviteType,
      token,
      weeklyPlanId,
      recipeId,
      sharedBy,
      householdName,
      search: location.search,
    };
    localStorage.setItem(PENDING_INVITE_KEY, JSON.stringify(payload));
  };

  const clearPendingInvite = () => {
    localStorage.removeItem(PENDING_INVITE_KEY);
  };

  const targetPath = useMemo(() => {
    if (inviteType === 'household') return null;
    if (hasValidWeeklyPlanId) return `/MealPlan/Recipe/${encodeURIComponent(String(weeklyPlanId))}?shared=true`;
    if (hasValidRecipeId) return `/recipe/${encodeURIComponent(String(recipeId))}?shared=true`;
    return null;
  }, [hasValidRecipeId, hasValidWeeklyPlanId, inviteType, weeklyPlanId, recipeId]);

  const completeAcceptance = useCallback(async () => {
    setProcessing(true);
    try {
      if (inviteType === 'household') {
        if (!token) {
          toast.error('Invitation token is missing.');
          return;
        }
        await apiPost(`/households/invitations/${encodeURIComponent(token)}/accept`);
        toast.success('Invitation accepted');
        clearPendingInvite();
        navigate('/MealPlan', { replace: true });
      } else {
        if (!targetPath) {
          toast.error('No recipe found in this invitation.');
          return;
        }
        clearPendingInvite();
        navigate(targetPath, { replace: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Could not accept invitation';
      toast.error(msg);
    } finally {
      setProcessing(false);
    }
  }, [inviteType, navigate, targetPath, token]);

  const handleAccept = async () => {
    if (!user) {
      persistPendingInvite();
      setShowLoginRequired(true);
      return;
    }
    await completeAcceptance();
  };

  const handleDecline = () => {
    clearPendingInvite();
  };

  // Resume pending invite after authentication
  useEffect(() => {
    if (!user || resumeAttemptedRef.current) return;
    const stored = localStorage.getItem(PENDING_INVITE_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored);
      resumeAttemptedRef.current = true;
      if (parsed?.search && parsed.search !== location.search) {
        navigate(`/invite${parsed.search}`, { replace: true });
        return;
      }
      if (parsed?.inviteType === 'recipe') {
        clearPendingInvite();
        return;
      }
      completeAcceptance();
    } catch {
      // If parse fails, clear pending data
      clearPendingInvite();
    }
  }, [completeAcceptance, location.search, navigate, user]);

  useEffect(() => {
    if (inviteType !== 'recipe') return;
    if (!user) return;
    if (!targetPath) return;
    if (recipeAccessState !== 'member') return;
    navigate(targetPath, { replace: true });
  }, [inviteType, navigate, recipeAccessState, targetPath, user]);

  const renderPrivateRecipeState = () => {
    return (
      <div className="min-h-screen bg-[#F8F7F5] flex items-center justify-center p-6">
        <div className="w-full max-w-sm bg-white rounded-[28px] border border-[#E6E2DD] shadow-sm p-6 text-center">
          <div className="w-[52px] h-[52px] rounded-full bg-[#F0F3EC] flex items-center justify-center mx-auto mb-4">
            <UtensilsCrossed className="w-6 h-6 text-[#4A7C59]" />
          </div>
          <h1 className="text-[26px] leading-tight text-[#2C2C2C] mb-2">
            This recipe is part of a private household
          </h1>
          <p className="text-[15px] leading-7 text-[#757575] mb-5">
            This link points to a recipe inside someone&apos;s household on MealPlan Genie. It's not publicly available.
          </p>
          <p className="text-[15px] leading-7 text-[#757575] mb-5">
             If you were expecting a shared recipe, please ask that person to include you in their household.
          </p>

          <div className="space-y-2">
            <button
              type="button"
              onClick={() => navigate('/MealPlan', { replace: true })}
              className="w-full py-3 bg-[#AFBB8B] text-[#1F2814] rounded-xl hover:bg-[#9FAA7B] transition-colors font-medium"
            >
              Back to Meal Plan
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (inviteType === 'recipe' && user && recipeAccessState === 'join-required') {
    return (
      <>
        <Toaster position="top-center" richColors closeButton />
        {renderPrivateRecipeState()}
      </>
    );
  }

  if (inviteType === 'recipe' && user && recipeAccessState === 'invite-required') {
    return (
      <>
        <Toaster position="top-center" richColors closeButton />
        {renderPrivateRecipeState()}
      </>
    );
  }

  if (inviteType === 'recipe' && user && recipeAccessState === 'member') {
    return (
      <div className="min-h-screen bg-[#F8F7F5] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm p-6 text-center">
          <p className="text-[#757575]">Opening recipe...</p>
        </div>
      </div>
    );
  }

  if (inviteType === 'recipe' && user && recipeAccessState === 'error') {
    return (
      <>
        <Toaster position="top-center" richColors closeButton />
        <div className="min-h-screen bg-[#F8F7F5] flex items-center justify-center p-6">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-sm p-6 text-center">
            <button
              onClick={() => navigate('/MealPlan', { replace: true })}
              className="mb-4 inline-flex items-center gap-2 text-[#757575] hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>
            <h1 className="text-xl mb-3">Unable to open this recipe</h1>
            <p className="text-[#757575]">Try again later or ask the household owner to resend the link.</p>
          </div>
        </div>
      </>
    );
  }

  if (showLoginRequired && !user) {
    return (
      <>
        <Toaster position="top-center" richColors closeButton />
        <LoginRequired
          recipe={inviteType === 'recipe' ? recipe : null}
          sharedBy={sharedBy}
          inviteType={inviteType === 'household' ? 'household' : 'recipe'}
          householdName={householdName}
          onSignIn={() => {
            persistPendingInvite();
            navigate('/auth/signin');
          }}
          onSignUp={() => {
            persistPendingInvite();
            navigate('/auth/signup');
          }}
        />
      </>
    );
  }

  return (
    <>
      <Toaster position="top-center" richColors closeButton />
      <InvitationScreen
        inviteType={inviteType}
        sharedBy={sharedBy}
        householdName={householdName}
        recipe={inviteType === 'recipe' ? recipe : null}
        onAccept={handleAccept}
        onDecline={handleDecline}
        loading={loadingRecipe || processing}
      />
    </>
  );
}

export default InvitationPage;
